import type { ChatStatus } from "ai";
import {
  ArrowUp,
  AtSign,
  Building2,
  ClipboardList,
  Clock,
  FileText,
  Folder,
  Lightbulb,
  ListChecks,
  Package,
  Paperclip,
  Settings2,
  SlashSquare,
  Sparkles,
  Square,
  TextQuote,
  X
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type ComponentType,
  type DragEvent,
  type FC,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { toast } from "sonner";

import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSelect,
  PromptInputSelectContent,
  PromptInputSelectItem,
  PromptInputSelectTrigger,
  PromptInputSelectValue,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputController
} from "@/components/ai-elements/prompt-input";
import { AgentRobot } from "@/components/brand";
import {
  activeMentionAt,
  appendMention,
  EMPTY_MODEL,
  filterMentions,
  insertMentionAt,
  mentionCandidates,
  modelSegments,
  parseMentions,
  reconcileDisplay,
  targetOf,
  toWire,
  type ActiveMention,
  type ComposerModel,
  type MentionCandidate,
  type MentionTarget,
  type TeamMember
} from "@/components/mentions/mentions";
import { MentionPicker } from "@/components/mentions/MentionPicker";
import { Button } from "@/components/ui/button";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Tag } from "@/components/ui/tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ComposerAttachmentChip } from "@/features/agents/attachments/components/AttachmentChip";
import { ComposerAttachmentTray } from "@/features/agents/attachments/components/ComposerAttachmentTray";
import type { AttachmentFilePart } from "@/features/agents/attachments/types";
import { useAgentAttachments } from "@/features/agents/attachments/useAgentAttachments";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_TRAY_THRESHOLD
} from "@/features/agents/attachments/utils";
import { ModelSelect } from "@/features/agents/components/ModelSelect";
import {
  ASSISTANT_PERSONA,
  type AgentModel,
  type AgentScope,
  type AutoMode,
  type OptimizeFor
} from "@/features/agents/types";
import { activeTurnCopy } from "@/features/agents/useAgentChat";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import type { ExploreGroup } from "@/features/automations/board/caseExploreStarters";
import type { StarterSkill } from "@/features/automations/components/skillModel";
import type { FirmSkillSummary } from "@/features/firmKnowledge/types";
import { useTrackerStore } from "@/features/timeTracking/tracker/trackerStore";
import { useSessionDraft } from "@/lib/sessionDraft";
import { cn } from "@/lib/utils";

import { useCaseIdeOptional } from "../../context/CaseIdeContext";
import { useCaseDataOptional } from "../../data/CaseDataContext";
import type { AgentMode, Artifact, ArtifactKind } from "../../types";
import type { ActiveTurn } from "../api/caseAgentApi";
import { SkillPicker } from "./SkillPicker";

/**
 * A `/` command. `insert` seeds the composer with text (a starter prompt);
 * `run` performs an action instead (new chat, switch mode). Exactly one.
 */
export type SlashCommand = {
  id: string;
  label: string;
  hint?: string;
  /** Menu section ("Firm", "Built-In"); consecutive commands share a heading. */
  group?: string;
  insert?: string;
  run?: () => void;
};

export type ComposerContextChip = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  /** "Intake template": the chip's tooltip. */
  kind: string;
  /** "2 selected", after the label. */
  detail?: string | null;
  onRemove: () => void;
};

/** A kept draft back as a model, or null when it isn't one. */
const parseDraftModel = (raw: string): ComposerModel | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as ComposerModel).display === "string" &&
      Array.isArray((parsed as ComposerModel).mentions)
    )
      return parsed as ComposerModel;
  } catch {
    // Not JSON: nothing to restore.
  }
  return null;
};

type Props = {
  status: ChatStatus;
  disabled?: boolean;
  /** `files`: the message's uploaded attachments (only with `attachmentScope`). */
  onSend: (text: string, files?: AttachmentFilePart[]) => void;
  /**
   * Stop the running turn: ours, or (in a team thread) a teammate's. Shown
   * while either is running.
   */
  onStop: () => void;
  /**
   * Hold a message for after the running turn (Cursor's Enter while busy).
   * Submitting while a turn runs (ours or a teammate's) queues instead of
   * sending.
   */
  onQueue: (text: string, files?: AttachmentFilePart[]) => void;
  /**
   * ⌘⏎ / Ctrl+⏎ while a turn runs: deliver the message into it at its next
   * step instead of queueing. Falls back to `onSend` when idle.
   */
  onSendNow?: (text: string, files?: AttachmentFilePart[]) => void;
  /**
   * The chat's scope: files can be attached (paperclip, drop, paste) and are
   * uploaded there as soon as they are picked. Omit for no attachments.
   */
  attachmentScope?: AgentScope | null;
  /** Pinned text the attorney highlighted in the editor; sent with the turn. */
  pinnedSelection?: string | null;
  onClearPinnedSelection?: () => void;
  /** The record open on the page, sent with the turn; removing it drops it. */
  contextChip?: ComposerContextChip | null;
  /** Agent (acts) or Plan (reads, asks, writes a plan). */
  mode: AgentMode;
  onModeChange: (mode: AgentMode) => void;
  /** Firm members offered under `@` (People group). */
  teamMembers?: TeamMember[];
  /** The signed-in user's uid, left out of the People group. */
  meUid?: string | null;
  /**
   * Someone else's agent turn on this thread. Agent-addressed sends are held
   * until it finishes; team messages still go through.
   */
  activeTurn?: ActiveTurn | null;
  /**
   * What `@Casey` addresses and the placeholder names. Defaults to Casey.
   */
  agentLabel?: string;
  /** Hide the Agent/Plan selector (agents without plan mode). */
  showModeSelect?: boolean;
  /**
   * The firm's active skills, offered by the skill chip after the mode select
   * (orthogonal to the mode: Plan + skill plans the skill's steps, Agent +
   * skill runs them). Omit to hide the chip.
   */
  skills?: FirmSkillSummary[];
  /** The chip's skill, if any (the next message's, or pinned). */
  skillId?: string | null;
  onSkillChange?: (skillId: string | null) => void;
  /** The chip's skill stays for every later turn. */
  skillPinned?: boolean;
  onSkillPinChange?: (pinned: boolean) => void;
  /** Shown after the skill chip (the Preferences chip). */
  preferencesControl?: ReactNode;
  /** Run a skill on this case now (records a run and sends the seed message). */
  onRunSkill?: (skill: FirmSkillSummary) => void;
  /** Case Explore groups, shown the way the Case Automations board groups them. */
  exploreGroups?: ExploreGroup[];
  /** Run an Explore starter on this case by sending its steps. */
  onRunStarter?: (starter: StarterSkill) => void;
  /** Firm display name for the skill picker's group heading. */
  firmName?: string | null;
  /**
   * The model picker (Cursor's), shown after the mode select when there is
   * more than Auto to offer. `model` is a catalog id, `null` Auto.
   */
  models?: AgentModel[];
  autoModes?: AutoMode[];
  model?: string | null;
  thinking?: boolean;
  optimizeFor?: OptimizeFor;
  onModelChange?: (model: string | null) => void;
  onThinkingChange?: (thinking: boolean) => void;
  onOptimizeForChange?: (optimizeFor: OptimizeFor) => void;
  /**
   * Where the unsent text is kept for this tab (`useSessionDraft`), so a
   * reload gives it back. Omit to keep nothing.
   */
  draftKey?: string | null;
  /**
   * Commands offered when the text starts with `/` (the workspace's starters
   * and actions). Omit to leave `/` as ordinary text.
   */
  slashCommands?: SlashCommand[];
  /** Overrides the mode/agent-derived placeholder. */
  placeholder?: string;
  /**
   * The thread already has messages (or a turn is running): the placeholder
   * reads "Add a follow-up" like Cursor's instead of the first-message copy.
   */
  followUp?: boolean;
  autoFocus?: boolean;
  /**
   * Lets a host outside the Case IDE (the Requests IDE) push a whole prompt
   * into the composer, as the IDE's empty state does through its context.
   */
  onPromptRequest?: (register: (text: string) => void) => () => void;
};

/** Commands whose label or hint contains the query, in order. */
export const filterSlashCommands = (
  commands: SlashCommand[],
  query: string
): SlashCommand[] => {
  const q = query.trim().toLowerCase();
  if (!q) return commands;
  return commands.filter(
    (c) =>
      c.label.toLowerCase().includes(q) ||
      (c.hint ?? "").toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q)
  );
};

/** The `/query` at the start of the text, or null when not in command mode. */
export const slashQueryOf = (text: string): string | null => {
  const m = /^\/(\S*)$/.exec(text);
  return m ? m[1] : null;
};

const KIND_ICON: Record<ArtifactKind, typeof FileText> = {
  file: FileText,
  card: Sparkles,
  draft: FileText,
  packet: Package,
  form: ClipboardList,
  settings: Settings2,
  plan: ListChecks,
  key_facts: Lightbulb,
  time: Clock,
  company: Building2,
  workflows: Sparkles
};

const MODES: {
  value: AgentMode;
  label: string;
  icon: typeof ListChecks;
  hint: string;
}[] = [
  {
    value: "agent",
    label: "Agent",
    icon: AgentRobot,
    hint: "Acts on the case"
  },
  {
    value: "plan",
    label: "Plan",
    icon: ListChecks,
    hint: "Reads, asks, and writes a plan"
  }
];

/**
 * The box itself, Cursor's: an elevated card with a 1px inset ring that
 * darkens a little while the textarea has focus, and the composer shadow. (InputGroup's default is a 3px ring, which
 * reads as a form field, and its own border would double ours.) InputGroup
 * also dims itself when *any* descendant is disabled — which the send button
 * is until something is typed — so that is switched off: the box looks the
 * same empty or full.
 */
const BOX =
  "rounded-xl border-transparent bg-elevated shadow-composer inset-ring inset-ring-line-3 dark:bg-elevated has-disabled:bg-elevated has-disabled:opacity-100 dark:has-disabled:bg-elevated has-[[data-slot=input-group-control]:focus-visible]:border-transparent has-[[data-slot=input-group-control]:focus-visible]:inset-ring-line-4 has-[[data-slot=input-group-control]:focus-visible]:ring-0";

/**
 * Footer mode select, Cursor's: a ghost picker with the mode's icon, its name
 * and a tiny chevron, filled only on hover or while open.
 */
const MODE_TRIGGER =
  "h-6 gap-1 rounded-full border-transparent bg-transparent px-2 pr-1.5 text-sm text-fg-2 shadow-none hover:bg-fill-1 hover:text-fg aria-expanded:bg-fill-2 aria-expanded:text-fg data-[size=sm]:h-6 data-[size=sm]:rounded-full dark:bg-transparent dark:hover:bg-fill-1 [&>svg:last-child]:size-3 [&>svg:last-child]:text-fg-3";

/**
 * Plan mode turns the picker's text amber, as Cursor does. The box itself
 * stays neutral; only the picker and the send button say which mode is on.
 */
const MODE_TRIGGER_PLAN =
  "text-warn hover:bg-warn-soft hover:text-warn aria-expanded:bg-warn-soft aria-expanded:text-warn [&>svg:last-child]:text-warn";

/**
 * Round action button: send arrow when idle, stop square while working. With
 * nothing typed the send arrow sits on a gray fill; with text it is ink (amber
 * in Plan mode, matching the picker).
 */
const ACTION_BUTTON =
  "size-6 rounded-full transition-colors disabled:opacity-100 [&_svg]:shrink-0";
const ACTION_BUTTON_IDLE =
  "bg-fill-3 text-fg-4 hover:bg-fill-3 hover:text-fg-4 disabled:bg-fill-3 disabled:text-fg-4";

const TOOL = "size-6 rounded-md text-fg-3 hover:bg-fill-1 hover:text-fg";
const ACTION_BUTTON_LIVE =
  "bg-fg text-elevated hover:bg-fg/85 hover:text-elevated disabled:bg-fg disabled:text-elevated";
const ACTION_BUTTON_LIVE_PLAN =
  "bg-warn text-white hover:bg-warn/90 hover:text-white disabled:bg-warn disabled:text-white";

const placeholderFor = (
  mode: AgentMode,
  agentLabel: string,
  inIde: boolean,
  followUp: boolean
): string =>
  mode === "plan"
    ? "Describe what you want to plan…"
    : followUp
    ? "Add a follow-up"
    : inIde
    ? `Ask ${agentLabel}. @ to mention a teammate, file or draft.`
    : `Ask ${agentLabel}. @ to mention a teammate.`;

const isAgentMode = (v: string): v is AgentMode =>
  v === "agent" || v === "plan";

/**
 * Shared by the textarea and its highlight layer so their glyphs line up.
 * The explicit leading pins the line-height both sides.
 */
const TEXT_METRICS =
  "px-2.5 py-2 text-base leading-5 [scrollbar-gutter:stable]";

/** Same palette as the transcript's mention chips (ChatMessage). */
const PILL_STYLE: Record<MentionTarget["kind"], string> = {
  agent: "bg-primary/15 text-primary dark:bg-primary/25",
  user: "bg-info-soft text-info",
  artifact: "bg-muted text-fg-2"
};

const EMPTY_NODES: Parameters<typeof mentionCandidates>[0] = [];
const EMPTY_LABELS: Parameters<typeof mentionCandidates>[1] = {};
const EMPTY_SECTIONS: Parameters<typeof mentionCandidates>[2] = [];

/**
 * The prompt box: `@` opens a picker over files and workspace items, trees can
 * push mentions in, the empty state can seed a prompt, and the editor's
 * selection shows as a removable chip.
 */
export const Composer: FC<Props> = ({
  status,
  disabled,
  onSend,
  onStop,
  pinnedSelection,
  onClearPinnedSelection,
  contextChip,
  mode,
  onModeChange,
  teamMembers = [],
  meUid = null,
  activeTurn = null,
  agentLabel = ASSISTANT_PERSONA,
  showModeSelect = true,
  skills,
  skillId = null,
  onSkillChange,
  skillPinned = false,
  onSkillPinChange,
  preferencesControl,
  onRunSkill,
  exploreGroups,
  onRunStarter,
  firmName,
  models,
  autoModes,
  model: pickedModel = null,
  thinking = false,
  optimizeFor = "balanced",
  onModelChange,
  onThinkingChange,
  onOptimizeForChange,
  draftKey = null,
  slashCommands,
  placeholder,
  followUp = false,
  autoFocus,
  onQueue,
  onSendNow,
  attachmentScope,
  onPromptRequest: onExternalPrompt
}) => {
  const controller = usePromptInputController();
  const attachments = useAgentAttachments(attachmentScope);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Cmd+. (Ctrl+. elsewhere) flips Agent <-> Plan like Cursor.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "." || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey)
        return;
      e.preventDefault();
      onModeChange(modeRef.current === "agent" ? "plan" : "agent");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onModeChange]);
  // Cmd+Shift+Backspace (Ctrl+Shift+Backspace elsewhere) stops the running
  // turn, Cursor's "Cancel generation". Only while one runs.
  const stopRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (
        e.key !== "Backspace" ||
        !(e.metaKey || e.ctrlKey) ||
        !e.shiftKey ||
        e.altKey
      )
        return;
      if (!stopRef.current) return;
      e.preventDefault();
      stopRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // Outside the Case IDE (the agent panel) there is no editor bridge and no
  // files to mention; people and the agent still are.
  const ide = useCaseIdeOptional();
  const caseData = useCaseDataOptional();
  const onMentionRequest = ide?.onMentionRequest;
  const onPromptRequest = ide?.onPromptRequest ?? onExternalPrompt;
  const fsNodes = caseData?.fs.nodes ?? EMPTY_NODES;
  const fsLabels = caseData?.fs.labels ?? EMPTY_LABELS;
  const sections = caseData?.workspace.sections ?? EMPTY_SECTIONS;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  // The textarea holds display text ("hi @Alice Smith "); the model also
  // remembers where the pills are so `toWire` can expand them to tokens on
  // send. Anything that changed the controller's text behind our back (the
  // provider's clear() after submit, for one) is folded in here.
  const modelRef = useRef<ComposerModel>(EMPTY_MODEL);
  const { value } = controller.textInput;
  if (modelRef.current.display !== value) {
    modelRef.current = reconcileDisplay(modelRef.current, value, {
      atomicDelete: false
    }).model;
  }
  const model = modelRef.current;
  const [mention, setMention] = useState<ActiveMention | null>(null);
  const [highlight, setHighlight] = useState(0);

  // A tree that never loaded (a cold-load blip) leaves nothing to mention;
  // opening `@` asks the server again rather than finding nothing.
  const fsLoaded = caseData?.fs.loaded ?? true;
  const refreshFs = caseData?.fs.refresh;
  const mentionOpen = mention !== null;
  useEffect(() => {
    if (mentionOpen && !fsLoaded && refreshFs) void refreshFs();
  }, [mentionOpen, fsLoaded, refreshFs]);

  const candidates = useMemo(
    () =>
      mentionCandidates(fsNodes, fsLabels, sections, teamMembers, meUid, {
        agentHint: `Ask ${agentLabel}`
      }),
    [fsNodes, fsLabels, sections, teamMembers, meUid, agentLabel]
  );
  const matches = useMemo(
    () => (mention ? filterMentions(candidates, mention.query) : []),
    [candidates, mention]
  );

  // Caret moves are applied once React has written the new value into the
  // textarea (a layout effect), not on a later frame, so they cannot race
  // with the next keystroke.
  const pendingCaretRef = useRef<{ caret?: number; reopen?: boolean } | null>(
    null
  );
  const placeCaret = useCallback((caret?: number, reopen = false) => {
    pendingCaretRef.current = { caret, reopen };
  }, []);

  /** Adopt a new model: it becomes the textarea's text, caret placed after. */
  const commit = useCallback(
    (next: { model: ComposerModel; caret?: number }, reopen = false) => {
      modelRef.current = next.model;
      controller.textInput.setInput(next.model.display);
      placeCaret(next.caret, reopen);
    },
    [controller.textInput, placeCaret]
  );

  // Unsent text survives a reload, pills and all.
  useSessionDraft(
    draftKey,
    value.trim() ? JSON.stringify(model) : null,
    (saved) => {
      if (controller.textInput.value) return;
      const restored = parseDraftModel(saved);
      if (restored) commit({ model: restored, caret: restored.display.length });
    }
  );

  // Trees push mentions; the empty state pushes whole prompts.
  useEffect(
    () =>
      onMentionRequest?.((artifact: Artifact) =>
        commit(appendMention(modelRef.current, artifact))
      ),
    [onMentionRequest, commit]
  );
  useEffect(
    () =>
      onPromptRequest?.((text) =>
        commit({
          model: reconcileDisplay(EMPTY_MODEL, text).model,
          caret: text.length
        })
      ),
    [onPromptRequest, commit]
  );

  const syncMention = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const next = activeMentionAt(
      el.value,
      el.selectionStart ?? el.value.length,
      modelRef.current.mentions
    );
    setMention((prev) =>
      prev?.start === next?.start && prev?.query === next?.query ? prev : next
    );
    if (!next || next.query !== mention?.query) setHighlight(0);
  }, [mention?.query]);

  // Free-form typing: keep the pills in step with the new text. A Backspace
  // into a pill removes it whole, in which case the textarea is corrected.
  const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.currentTarget.value;
    const out = reconcileDisplay(modelRef.current, next);
    modelRef.current = out.model;
    if (out.model.display !== next) {
      controller.textInput.setInput(out.model.display);
      setMention(null);
      placeCaret(out.caret);
      return;
    }
    syncMention();
  };

  const choose = useCallback(
    (c: MentionCandidate) => {
      const el = textareaRef.current;
      if (!el || !mention) return;
      setMention(null);
      commit(
        insertMentionAt(
          modelRef.current,
          mention,
          el.selectionStart ?? el.value.length,
          targetOf(c)
        )
      );
    },
    [mention, commit]
  );

  const startMention = () => {
    const cur = modelRef.current.display;
    const sep = cur && !/\s$/.test(cur) ? " " : "";
    commit(
      {
        model: reconcileDisplay(modelRef.current, `${cur}${sep}@`).model,
        caret: cur.length + sep.length + 1
      },
      true
    );
  };

  useLayoutEffect(() => {
    const pending = pendingCaretRef.current;
    const el = textareaRef.current;
    if (!pending || !el) return;
    pendingCaretRef.current = null;
    el.focus();
    if (pending.caret != null)
      el.setSelectionRange(pending.caret, pending.caret);
    if (pending.reopen) syncMention();
  });

  // `/` commands: only while the whole text is `/query`, and only when the
  // host offers commands.
  const slashQuery = slashCommands ? slashQueryOf(model.display) : null;
  const slashMatches = useMemo(
    () =>
      slashCommands && slashQuery !== null
        ? filterSlashCommands(slashCommands, slashQuery)
        : [],
    [slashCommands, slashQuery]
  );
  const [slashHighlight, setSlashHighlight] = useState(0);
  const [slashDismissed, setSlashDismissed] = useState(false);
  useEffect(() => {
    setSlashHighlight(0);
    if (slashQuery === null) setSlashDismissed(false);
  }, [slashQuery]);
  const slashOpen =
    slashQuery !== null && slashMatches.length > 0 && !slashDismissed;
  const runSlash = useCallback(
    (c: SlashCommand) => {
      if (c.run) {
        commit({ model: EMPTY_MODEL, caret: 0 });
        c.run();
        return;
      }
      const text = c.insert ?? "";
      commit({
        model: reconcileDisplay(EMPTY_MODEL, text).model,
        caret: text.length
      });
    },
    [commit]
  );

  const busy = status === "submitted" || status === "streaming";
  const parsed = useMemo(() => parseMentions(toWire(model)), [model]);
  const toAgent = parsed.addressedToAgent;
  // Another user's turn holds the agent; their own turns show as `busy`.
  const lockedByOther = Boolean(activeTurn && activeTurn.uid !== meUid);
  // Team messages still go through while someone else's turn runs.
  const sendBlocked = toAgent && lockedByOther && !busy;
  // Enter queues while a turn runs (ours or a teammate's), like Cursor.
  const queueing = busy || (toAgent && lockedByOther);
  // A turn is running (ours or anyone's): Stop is offered.
  const turnRunning = busy || Boolean(activeTurn);
  stopRef.current = turnRunning ? onStop : null;
  useEffect(
    () => (busy ? useTrackerStore.getState().startAgentTurn() : undefined),
    [busy]
  );

  const hasFiles = attachments.items.length > 0;
  /**
   * The uploaded files to send with this message, or null when it cannot go
   * yet: a file is still uploading, or the message is for people only (files
   * are for the agent).
   */
  const takeFiles = (): AttachmentFilePart[] | null => {
    if (attachments.uploading) {
      toast.info("Wait for the files to finish uploading");
      return null;
    }
    if (attachments.parts.length && !toAgent) {
      toast.error("Files go to the agent: mention it to send them");
      return null;
    }
    return attachments.parts;
  };
  const deliver = (
    fn: (text: string, files?: AttachmentFilePart[]) => void,
    wire: string,
    files: AttachmentFilePart[]
  ) => {
    if (files.length) fn(wire, files);
    else fn(wire);
    attachments.clear();
  };
  const pickFiles = (list: FileList | null) => {
    if (list?.length) attachments.add(list);
  };
  const onDropFiles = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.files?.length) return;
    // Ours, not PromptInput's own (blob URL) attachments.
    e.preventDefault();
    e.stopPropagation();
    pickFiles(e.dataTransfer.files);
  };
  const onPasteFiles = (e: ClipboardEvent<HTMLDivElement>) => {
    const pasted = Array.from(e.clipboardData?.items ?? [])
      .filter((i) => i.kind === "file")
      .map((i) => i.getAsFile())
      .filter((f): f is File => f !== null);
    if (!pasted.length) return;
    e.preventDefault();
    e.stopPropagation();
    attachments.add(pasted);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // ⌘⏎ / Ctrl+⏎: "send now". While a turn runs the message goes into it at
    // its next step; when idle it is an ordinary send.
    if (
      e.key === "Enter" &&
      (e.metaKey || e.ctrlKey) &&
      !slashOpen &&
      !(mention && matches.length)
    ) {
      e.preventDefault();
      const wire = toWire(modelRef.current);
      if ((!wire.trim() && !hasFiles) || disabled) return;
      const files = takeFiles();
      if (!files || (!wire.trim() && !files.length)) return;
      setMention(null);
      if (queueing) deliver(onSendNow ?? onQueue, wire, files);
      else if (!sendBlocked) deliver(onSend, wire, files);
      commit({ model: EMPTY_MODEL, caret: 0 });
      return;
    }
    if (slashOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSlashHighlight((h) => (h + 1) % slashMatches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSlashHighlight(
          (h) => (h - 1 + slashMatches.length) % slashMatches.length
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        runSlash(slashMatches[slashHighlight] ?? slashMatches[0]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setSlashDismissed(true);
        return;
      }
    }
    if (!mention || !matches.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      choose(matches[highlight] ?? matches[0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMention(null);
    }
  };

  // Plan mode colors the picker and the send/stop button amber.
  const planTone = mode === "plan";
  const actionLive = planTone ? ACTION_BUTTON_LIVE_PLAN : ACTION_BUTTON_LIVE;

  // The agent-mode chip doubles as a status light: thinking while the request
  // is in flight, speaking while the reply streams.
  const modeIcon =
    mode === "plan" ? (
      <ListChecks className="size-3" />
    ) : (
      <AgentRobot
        state={
          status === "streaming"
            ? "speaking"
            : status === "submitted"
            ? "thinking"
            : "idle"
        }
        className="size-3.5"
      />
    );
  const text = model.display;
  const segments = useMemo(() => modelSegments(model), [model]);

  const attachButton = (className: string) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className={className}
          aria-label="Attach files"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          data-testid="composer-attach"
        >
          <Paperclip className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        Attach files (PDF, images, Word, Excel, text)
      </TooltipContent>
    </Tooltip>
  );
  const mentionButton = (className: string) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className={className}
          aria-label="Mention"
          onClick={startMention}
          disabled={disabled}
          data-testid="composer-at"
        >
          <AtSign className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {ide
          ? "Mention a teammate, file or draft"
          : "Mention a teammate or agent"}
      </TooltipContent>
    </Tooltip>
  );
  const sendClass = (live: boolean) =>
    cn(ACTION_BUTTON, live ? actionLive : ACTION_BUTTON_IDLE);
  const sendIcon = <ArrowUp className="size-3.5 text-current" />;

  const modelControl =
    models?.length && onModelChange ? (
      <ModelSelect
        model={pickedModel}
        thinking={thinking}
        optimizeFor={optimizeFor}
        models={models}
        autoModes={autoModes}
        onModelChange={onModelChange}
        onThinkingChange={onThinkingChange ?? (() => undefined)}
        onOptimizeForChange={onOptimizeForChange ?? (() => undefined)}
        disabled={disabled}
        className="h-6 rounded-full px-2 font-normal hover:bg-fill-1 data-[state=open]:bg-fill-2"
      />
    ) : null;
  const skillControl =
    skills && onSkillChange ? (
      <SkillPicker
        skills={skills}
        skillId={skillId ?? null}
        onSkillChange={onSkillChange}
        pinned={skillPinned}
        onPinChange={onSkillPinChange}
        onRunSkill={onRunSkill}
        exploreGroups={exploreGroups}
        onRunStarter={onRunStarter}
        firmName={firmName}
        disabled={disabled}
      />
    ) : null;
  const agentControls = (
    <>
      {showModeSelect ? (
        <PromptInputSelect
          value={mode}
          onValueChange={(v) => {
            if (isAgentMode(v)) onModeChange(v);
          }}
          disabled={disabled}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <PromptInputSelectTrigger
                size="sm"
                aria-label="Mode"
                data-testid="mode-select"
                data-mode={mode}
                className={cn(MODE_TRIGGER, planTone && MODE_TRIGGER_PLAN)}
              >
                <PromptInputSelectValue>
                  {modeIcon}
                  <span>
                    {MODES.find((m) => m.value === mode)?.label ?? mode}
                  </span>
                </PromptInputSelectValue>
              </PromptInputSelectTrigger>
            </TooltipTrigger>
            <TooltipContent side="top" className="flex items-center gap-2">
              Switch mode
              <KbdGroup>
                <Kbd>⌘</Kbd>
                <Kbd>.</Kbd>
              </KbdGroup>
            </TooltipContent>
          </Tooltip>
          <PromptInputSelectContent align="start">
            {MODES.map((m) => (
              <PromptInputSelectItem
                key={m.value}
                value={m.value}
                className="text-xs"
              >
                {m.value === "agent" ? (
                  <AgentRobot state="static" className="size-3.5" />
                ) : (
                  <m.icon className="size-3.5 text-warn" />
                )}
                <span>{m.label}</span>
                <span className="ml-1 text-xs text-muted-foreground">
                  {m.hint}
                </span>
              </PromptInputSelectItem>
            ))}
          </PromptInputSelectContent>
        </PromptInputSelect>
      ) : null}
      {skillControl}
      {preferencesControl}
      {modelControl}
    </>
  );

  return (
    <div
      className="relative"
      onDropCapture={attachments.enabled ? onDropFiles : undefined}
      onPasteCapture={attachments.enabled ? onPasteFiles : undefined}
    >
      {slashOpen ? (
        <div
          role="listbox"
          aria-label="Commands"
          data-testid="slash-commands"
          className="absolute inset-x-2 bottom-full z-20 mb-1 max-h-72 overflow-y-auto rounded-md border bg-popover p-1 shadow-md"
        >
          {slashMatches.map((c, i) => [
            i === 0 || c.group !== slashMatches[i - 1].group ? (
              <div
                key={`heading-${c.group ?? ""}-${i}`}
                role="presentation"
                className="px-2 pt-1.5 pb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                {c.group ?? "Commands"}
              </div>
            ) : null,
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={i === slashHighlight}
              data-testid={`slash-command-${c.id}`}
              // Keep the caret in the composer; the click runs the command,
              // the same path as Enter.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => runSlash(c)}
              onMouseEnter={() => setSlashHighlight(i)}
              className={cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-xs",
                i === slashHighlight
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-muted"
              )}
            >
              <SlashSquare className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{c.label}</span>
              {c.hint ? (
                <span className="shrink-0 truncate text-xs text-muted-foreground">
                  {c.hint}
                </span>
              ) : null}
            </button>
          ])}
        </div>
      ) : null}
      {mention && matches.length ? (
        <MentionPicker
          matches={matches}
          highlight={highlight}
          onHighlight={setHighlight}
          onPick={choose}
          className="absolute inset-x-2 bottom-full z-20 mb-1"
          renderLeading={(c) => {
            if (c.kind === "agent")
              return (
                <AgentMoodAvatar
                  animated
                  size="xs"
                  className="rounded-xs"
                  agent={{
                    key: c.scope
                      ? `${c.scope.kind}:${c.scope.id}`
                      : attachmentScope
                      ? `${attachmentScope.kind}:${attachmentScope.id}`
                      : "assistant"
                  }}
                />
              );
            if (c.kind !== "artifact") return null;
            const Icon =
              c.artifact.kind === "file" && c.hint === "Folder"
                ? Folder
                : KIND_ICON[c.artifact.kind];
            return Icon ? (
              <Icon className="size-3.5 shrink-0 text-muted-foreground" />
            ) : null;
          }}
        />
      ) : null}

      <PromptInput
        onSubmit={() => {
          // The controller holds display text; the server gets the tokens.
          const wire = toWire(modelRef.current);
          if (!wire.trim() && !hasFiles) return;
          const files = takeFiles();
          // Throwing keeps the typed text (PromptInput clears on success).
          if (!files || (!wire.trim() && !files.length))
            throw new Error("not sent");
          if (queueing) {
            setMention(null);
            deliver(onQueue, wire, files);
            return;
          }
          if (sendBlocked) return;
          setMention(null);
          deliver(onSend, wire, files);
        }}
        className="rounded-lg bg-background shadow-none"
        groupClassName={BOX}
      >
        {pinnedSelection ? (
          <div className="flex items-start gap-2 border-b px-3 py-1.5 text-xs text-muted-foreground">
            <TextQuote className="mt-0.5 size-3 shrink-0" />
            <span className="line-clamp-2 min-w-0 flex-1 italic">
              {pinnedSelection}
            </span>
            <button
              type="button"
              aria-label="Remove selection"
              className="shrink-0 hover:text-foreground"
              onClick={onClearPinnedSelection}
            >
              <X className="size-3" />
            </button>
          </div>
        ) : null}
        {contextChip ? (
          <div
            className="flex w-full min-w-0 px-2.5 pt-2.5"
            data-testid="composer-context"
          >
            <Tag className="pr-0.5" title={contextChip.kind}>
              <contextChip.icon className="text-fg-3" />
              <span className="min-w-0 truncate">{contextChip.label}</span>
              {contextChip.detail ? (
                <span className="text-fg-3">{contextChip.detail}</span>
              ) : null}
              <button
                type="button"
                aria-label="Remove From Context"
                data-testid="composer-context-remove"
                className="inline-flex size-4 items-center justify-center rounded-xs text-fg-3 hover:bg-fill-3 hover:text-fg"
                onClick={contextChip.onRemove}
              >
                <X />
              </button>
            </Tag>
          </div>
        ) : null}
        {attachments.items.length >= ATTACHMENT_TRAY_THRESHOLD ? (
          <div
            className="flex w-full px-2.5 pt-2.5"
            data-testid="composer-attachments"
          >
            <ComposerAttachmentTray
              items={attachments.items}
              onRemove={attachments.remove}
              onRemoveAll={attachments.removeAll}
            />
          </div>
        ) : hasFiles ? (
          <div
            className="flex w-full flex-wrap items-end gap-2 px-2.5 pt-2.5"
            data-testid="composer-attachments"
          >
            {attachments.items.map((item) => (
              <ComposerAttachmentChip
                key={item.localId}
                item={item}
                onRemove={() => attachments.remove(item.localId)}
              />
            ))}
          </div>
        ) : null}
        <PromptInputBody>
          {/*
           * The textarea's own text is transparent; this layer behind it
           * draws the same text with the mentions as pills. Both share font,
           * padding, line-height and wrapping so the glyphs line up, and the
           * pill's padding is cancelled by a negative margin so it adds no
           * width. Scroll position is mirrored from the textarea.
           */}
          <div className="relative w-full">
            <div
              ref={mirrorRef}
              aria-hidden
              data-testid="composer-mirror"
              className={cn(
                TEXT_METRICS,
                "pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words text-foreground",
                disabled && "opacity-50"
              )}
            >
              {segments.map((seg, i) =>
                seg.type === "text" ? (
                  <span key={i}>{seg.text}</span>
                ) : (
                  <span
                    key={i}
                    data-testid={`composer-pill-${seg.target.kind}`}
                    className={cn(
                      "-mx-0.5 rounded px-0.5 py-px",
                      PILL_STYLE[seg.target.kind]
                    )}
                  >
                    {seg.text}
                  </span>
                )
              )}
              {"\u200b"}
            </div>
            <PromptInputTextarea
              ref={textareaRef}
              autoFocus={autoFocus}
              placeholder={
                disabled
                  ? "Loading…"
                  : placeholder ??
                    placeholderFor(
                      mode,
                      agentLabel,
                      !!ide,
                      followUp || turnRunning
                    )
              }
              disabled={disabled}
              className={cn(
                TEXT_METRICS,
                "relative z-10 min-h-12 w-full bg-transparent text-transparent caret-foreground"
              )}
              onKeyDown={onKeyDown}
              onKeyUp={syncMention}
              onClick={syncMention}
              onChange={onTextChange}
              onScroll={(e) => {
                if (mirrorRef.current)
                  mirrorRef.current.scrollTop = e.currentTarget.scrollTop;
              }}
              onBlur={() => setTimeout(() => setMention(null), 120)}
            />
          </div>
        </PromptInputBody>
        <PromptInputFooter className="px-2 pb-1.5">
          <PromptInputTools>{agentControls}</PromptInputTools>
          <div className="flex items-center gap-1">
            {attachments.enabled ? (
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className="hidden"
                data-testid="composer-file-input"
                onChange={(e) => {
                  pickFiles(e.currentTarget.files);
                  e.currentTarget.value = "";
                }}
              />
            ) : null}
            {attachments.enabled ? attachButton(TOOL) : null}
            {mentionButton(TOOL)}
            {turnRunning ? (
              <>
                {text.trim() || hasFiles ? (
                  // Typed while a turn runs: Enter queues an agent message
                  // (⌘⏎ sends it now); a team message still just sends.
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="submit"
                        variant="ghost"
                        size="icon-xs"
                        className={sendClass(true)}
                        disabled={disabled || attachments.uploading}
                        aria-label={queueing ? "Queue" : "Send"}
                        data-testid="composer-submit"
                        data-mode={mode}
                      >
                        {sendIcon}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="flex items-center gap-2"
                    >
                      {queueing ? (
                        <>
                          <span className="flex items-center gap-1">
                            Queue <Kbd>⏎</Kbd>
                          </span>
                          <span className="text-xs opacity-70">·</span>
                          <span className="flex items-center gap-1">
                            Send now
                            <KbdGroup>
                              <Kbd>⌘</Kbd>
                              <Kbd>⏎</Kbd>
                            </KbdGroup>
                          </span>
                        </>
                      ) : (
                        <>
                          Send to the team <Kbd>⏎</Kbd>
                        </>
                      )}
                    </TooltipContent>
                  </Tooltip>
                ) : null}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className={cn(ACTION_BUTTON, actionLive)}
                      aria-label="Stop"
                      onClick={onStop}
                      data-testid="composer-stop"
                      data-mode={mode}
                    >
                      <Square className="size-3 fill-current text-current" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="flex items-center gap-2"
                  >
                    {lockedByOther && !busy
                      ? `Stop ${
                          activeTurn?.name ? `${activeTurn.name}'s` : "their"
                        } turn · ${activeTurnCopy(
                          activeTurn ?? {},
                          agentLabel
                        )}`
                      : "Stop"}
                    <KbdGroup>
                      <Kbd>⌘</Kbd>
                      <Kbd>⇧</Kbd>
                      <Kbd>⌫</Kbd>
                    </KbdGroup>
                  </TooltipContent>
                </Tooltip>
              </>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      type="submit"
                      variant="ghost"
                      size="icon-xs"
                      className={sendClass(!!text.trim() || hasFiles)}
                      disabled={
                        disabled ||
                        (!text.trim() && !hasFiles) ||
                        attachments.uploading
                      }
                      aria-label="Send"
                      data-testid="composer-submit"
                      data-mode={mode}
                    >
                      {sendIcon}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="flex items-center gap-2">
                  Send
                  <KbdGroup>
                    <Kbd>⏎</Kbd>
                    <span className="text-xs opacity-70">·</span>
                    <Kbd>⇧⏎</Kbd>
                    <span className="text-xs opacity-70">new line</span>
                  </KbdGroup>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </PromptInputFooter>
      </PromptInput>
    </div>
  );
};
